import { useEffect, useState } from 'react';
import { Button, Text, Title } from '@tremor/react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { logout } from '../api/auth';
import { fetchStores } from '../api/stores';
import { t } from '../lib/i18n';
import { useStoreFilter } from '../lib/storeFilter';
import ThemeToggle from './ThemeToggle';
import NavDropdown from './NavDropdown';
import StoreFilter from './StoreFilter';
import type { NavChild } from './NavDropdown';
import type { ReactNode } from 'react';
import type { Store, User } from '../api/types';
import { canApplyPayments, canComms, canManageTerminalUsers, canPacesetter, canPickStore, canViewDvi } from '../lib/roles';
import { payablesPerms } from '../lib/payablesPerms';

interface Props {
  user: User;
  right?: ReactNode;
  /**
   * Show the app-wide store picker (lib/storeFilter) in the right cluster.
   * Pages whose data honors a store opt in; the value persists across pages
   * so a store picked on the Resumen carries into Ventas, Partes, etc.
   * Only rendered for roles that can pick (owner/manager); hidden when the
   * site reports no stores. If the stores list can't be loaded but a store is
   * remembered, a minimal "Tienda N / Todas" picker still renders so the
   * filter is never applied invisibly.
   */
  storeFilter?: boolean;
}

function SharedStoreFilter({ user }: { user: User }) {
  const [sid, setSid] = useStoreFilter(user);
  const [stores, setStores] = useState<Store[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchStores()
      .then((s) => { if (alive) { setStores(s); setFailed(false); } })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, []);

  // A remembered store this site doesn't have (stale choice from another
  // install, or a store that was retired) would silently filter every page
  // down to nothing — drop it back to "all stores" as soon as we know.
  useEffect(() => {
    if (stores && sid !== null && !stores.some((s) => s.sid === sid)) setSid(null);
  }, [stores, sid, setSid]);

  if (stores) {
    if (stores.length === 0) return null;
    return (
      <span data-tour="store-filter">
        <StoreFilter stores={stores} value={sid} onChange={setSid} />
      </span>
    );
  }
  // Stores list unavailable (API/daemon hiccup; the fetch is retried on the
  // next mount). A remembered store would otherwise keep filtering every
  // page with no visible control to clear it, so offer a minimal picker that
  // names the store by number and still lists "all stores" as the way out.
  if (failed && sid !== null) {
    return (
      <span data-tour="store-filter">
        <StoreFilter
          stores={[{ sid, name: `${t.filter.store} ${sid}` }]}
          value={sid}
          onChange={setSid}
        />
      </span>
    );
  }
  return null;
}

// A top-level nav entry is either a flat link or a dropdown group.
type NavItem =
  | { kind: 'link'; to: string; label: string; show: boolean; end?: boolean }
  | { kind: 'group'; id: string; label: string; active: boolean; children: NavChild[] };

export default function Header({ user, right, storeFilter = false }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  async function onLogout() {
    try { await logout(); } catch { /* swallow — local nav still flows */ }
    navigate('/login');
  }

  const navClass = ({ isActive }: { isActive: boolean }) =>
    'text-sm px-2 py-1 rounded ' +
    (isActive
      ? 'bg-tremor-brand text-tremor-brand-inverted'
      : 'text-tremor-content hover:text-tremor-content-strong');

  // Mobile menu items reuse the desktop link list but with bigger touch
  // targets and a vertical layout. Closing on click prevents the menu from
  // staying open while the user navigates.
  const mobileNavClass = ({ isActive }: { isActive: boolean }) =>
    'block px-4 py-3 text-base ' +
    (isActive
      ? 'bg-tremor-brand text-tremor-brand-inverted'
      : 'text-tremor-content-strong hover:bg-tremor-background-muted');

  // counterman is a shared kiosk account whose only reachable page is
  // /pacesetter (see App.tsx's CountermanKioskRedirect) — it has no business
  // reason to see (or be tempted to tap into) any other menu item. Every
  // other role below is unaffected: this only ever short-circuits the nav
  // rendering, never any of the per-item `show` gates themselves.
  const isKiosk = user.role === 'counterman';

  // Accounting users see only finance-relevant pages — hide operational
  // scorecards (inventory health, vendor scorecard, salespeople performance,
  // lost sales). Backend handlers also 403 these routes for accounting; the
  // nav guard is just a UX nicety.
  const accountingHidden: ReadonlyArray<string> = ['inventory', 'vendors', 'salespeople', 'lost'];
  const isHidden = (key: string) =>
    user.role === 'accounting' && accountingHidden.includes(key);
  const isOwner = user.role === 'owner';
  // Service Shop ("Taller") is an optional module + a management view: shown
  // only when the customer runs it (features.service_shop) AND the user is
  // owner/manager (matches the server's view:service gate).
  const canService = !!user.features?.service_shop && (user.role === 'owner' || user.role === 'manager');
  const canCaja = user.role === 'owner' || user.role === 'manager' || user.role === 'accounting';
  // Anticipos write tab — only when writes are enabled and user is owner/accounting.
  const canAnticipo = (user.role === 'owner' || user.role === 'accounting') && !!user.features?.writes_enabled;
  // Recalcular min/max (JCD-56) — owner by default, delegable. The nav entry
  // is hidden when the user cannot use it rather than shown-and-refused.
  const canPriceUpload = !!user.features?.writes_enabled && (user.role === 'owner' || (user.permissions ?? []).includes('price:upload'));
  // Discrepancias (#209 inventory-change audit) — owner/manager default, or a
  // per-user view:stock-audit grant. Mirrors the server gate.
  const canStockAudit = user.role === 'owner' || user.role === 'manager' || (user.permissions ?? []).includes('view:stock-audit');
  // Inspecciones (DVI) — a shop-floor activity, so broader than Service:
  // owner/manager/staff by default (dvi:view), feature-gated on dvi_enabled.
  // canViewDvi lives in lib/roles.ts so the standalone /inspecciones page
  // (Inspecciones.tsx) can re-check the exact same gate on a direct URL hit.
  const canDvi = canViewDvi(user);
  // Comunicaciones (Service Comms outbox) — a management surface like
  // Service, not a shop-floor one like DVI: canComms defaults to
  // owner/manager only (comms:view), feature-gated on comms_enabled. Lives
  // in lib/roles.ts so the standalone /comunicaciones page can re-check the
  // exact same gate on a direct URL hit.
  const canCommsLink = canComms(user);
  // Payables: feature flag + ap:view. No writes_enabled term — the module
  // makes no ERP write (see lib/payablesPerms.ts).
  const canPayables = payablesPerms(user).canView;

  // Helper: is the current path one of (or under) these prefixes?
  const underAny = (paths: string[]) =>
    paths.some((p) => location.pathname === p || location.pathname.startsWith(p + '/'));

  // Ventas group — the sales page + sales-adjacent views: salespeople, lost
  // sales, cash close, and advances.
  const ventasChildren: NavChild[] = [
    { to: '/sales',      label: t.nav.sales,       show: true },
    { to: '/vendedores', label: t.nav.salespeople, show: !isHidden('salespeople') },
    { to: '/lost',       label: t.nav.lost,        show: !isHidden('lost') },
    { to: '/caja',       label: t.nav.caja,        show: canCaja },
    { to: '/anticipos',  label: t.nav.anticipos,   show: canAnticipo },
  ];

  // Inventario group — stock health, the parts catalog, the change audit, and
  // the periodic snapshots.
  const inventarioChildren: NavChild[] = [
    { to: '/inventory',     label: t.nav.inventory,     show: !isHidden('inventory') },
    { to: '/parts',         label: t.nav.parts,         show: true },
    { to: '/discrepancias', label: t.nav.discrepancias, show: canStockAudit },
    { to: '/archives',      label: t.nav.archives,      show: isOwner },
  ];

  // Clientes group — RFM segmentation + AR aging + the account statement.
  const clientesChildren: NavChild[] = [
    { to: '/customers', label: t.nav.customers, show: user.role !== 'staff' },
    { to: '/ar',        label: t.nav.cxcList,   show: true },
    { to: '/pagos',     label: t.nav.pagos,     show: canApplyPayments(user) },
    { to: '/estado',    label: t.nav.statement, show: user.role !== 'staff' },
  ];

  // Taller group — the service cockpit plus its shop-floor companions (DVI
  // inspections, customer comms). Each child keeps its own gate, so e.g.
  // staff (DVI only) see a one-item group and a customer without the module
  // sees no group at all.
  const tallerChildren: NavChild[] = [
    { to: '/service',        label: t.nav.service, show: canService },
    { to: '/inspecciones',   label: t.dvi.nav,     show: canDvi },
    { to: '/comunicaciones', label: t.comms.nav,   show: canCommsLink },
  ];

  // Administración group — users, terminal users, settings, and price uploads.
  const adminChildren: NavChild[] = [
    { to: '/admin/users',          label: t.nav.adminUsers,    show: isOwner },
    { to: '/admin/write-audit',    label: t.nav.writeAudit,    show: isOwner },
    { to: '/admin/terminal-users', label: t.nav.terminalUsers, show: canManageTerminalUsers(user) },
    { to: '/admin/config',         label: t.nav.config,        show: isOwner },
    { to: '/price-uploads',        label: t.nav.priceUploads,  show: canPriceUpload },
  ];

  // Ordered top-level nav: standalone links + four dropdown groups (down from
  // ~18 flat items). Móvil is intentionally NOT a desktop tab — owners auto-open
  // /movil on phones and it stays reachable directly; keeping it out of the bar
  // reduces clutter. A group whose children are all role-gated off renders empty
  // (NavDropdown / renderMobileGroup drop it), so no per-role special-casing.
  const navItems: NavItem[] = [
    { kind: 'link',  to: '/',           label: t.nav.overview,   show: true, end: true },
    { kind: 'link',  to: '/tablero',    label: t.nav.dashboard,  show: true },
    { kind: 'group', id: 'ventas',      label: t.nav.sales,      active: underAny(['/sales', '/vendedores', '/lost', '/caja', '/anticipos']), children: ventasChildren },
    { kind: 'group', id: 'inventario',  label: t.nav.inventory,  active: underAny(['/inventory', '/parts', '/discrepancias', '/archives']), children: inventarioChildren },
    { kind: 'link',  to: '/purchasing', label: t.nav.purchasing, show: !isHidden('vendors') },
    { kind: 'link',  to: '/pagar',      label: t.nav.cxp,        show: canPayables },
    { kind: 'group', id: 'clientes',    label: t.nav.customers,  active: underAny(['/customers', '/ar', '/pagos', '/estado']), children: clientesChildren },
    { kind: 'link',  to: '/reports',    label: t.nav.reports,    show: user.role !== 'staff' },
    { kind: 'group', id: 'admin',       label: t.nav.admin,      active: underAny(['/admin', '/price-uploads']), children: adminChildren },
    { kind: 'link',  to: '/pacesetter', label: t.nav.pacesetter, show: canPacesetter(user) },
    { kind: 'group', id: 'taller',      label: t.nav.service,    active: underAny(['/service', '/inspecciones', '/comunicaciones']), children: tallerChildren },
    // Customer ticket board (JCD-20/JCD-21) — no natural existing group, and
    // every authenticated (non-kiosk) role can see it: the server scopes which
    // tickets come back, not the nav.
    { kind: 'link',  to: '/soporte/tickets', label: t.nav.supportTickets, show: true },
  ];

  // Mobile group section: non-link label + indented child links.
  function renderMobileGroup(id: string, label: string, children: NavChild[]) {
    const visible = children.filter((c) => c.show);
    if (visible.length === 0) return null;
    return (
      <div key={id}>
        <div
          className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-tremor-content-subtle select-none"
          data-testid={`${id}-mobile-label`}
        >
          {label}
        </div>
        {visible.map((child) => (
          <NavLink
            key={child.to}
            to={child.to}
            className={({ isActive }) =>
              'block pl-8 pr-4 py-3 text-base ' +
              (isActive
                ? 'bg-tremor-brand text-tremor-brand-inverted'
                : 'text-tremor-content-strong hover:bg-tremor-background-muted')
            }
            onClick={() => setMenuOpen(false)}
          >
            {child.label}
          </NavLink>
        ))}
      </div>
    );
  }

  return (
    <header className="border-b border-tremor-border bg-tremor-background">
      <div className="px-4 sm:px-6 py-3 flex flex-wrap items-center gap-x-4 gap-y-2">
        <Title>{t.appName}</Title>

        {/* Desktop nav — hidden below xl breakpoint; the mobile drawer covers
            that case. xl chosen because all links + the right slot fit
            comfortably at >=1280px but start wrapping below. Suppressed
            entirely for the counterman kiosk — see isKiosk above. */}
        {!isKiosk && (
          // flex-1 + min-w-0 + flex-wrap: the nav takes the space between the
          // title and the right cluster and WRAPS its items to a second row
          // instead of overflowing the viewport. Without this the ~16 owner-level
          // links push the right cluster off-screen at ≤1500px (a common laptop
          // width), giving every page ~173px of horizontal scroll.
          <nav className="hidden xl:flex flex-1 min-w-0 flex-wrap items-center gap-1">
            {navItems.map((it) =>
              it.kind === 'group' ? (
                <NavDropdown
                  key={it.id}
                  id={it.id}
                  label={it.label}
                  active={it.active}
                  items={it.children}
                  navClass={navClass}
                />
              ) : it.show ? (
                <NavLink key={it.to} to={it.to} end={it.end} className={navClass}>{it.label}</NavLink>
              ) : null,
            )}
          </nav>
        )}

        <div className="ml-auto flex items-center gap-3">
          {storeFilter && canPickStore(user) && <SharedStoreFilter user={user} />}
          {right}
          <ThemeToggle />
          <Text className="text-tremor-content hidden sm:inline">
            {user.username} · {t.roles[user.role]}
          </Text>
          <Button variant="light" onClick={onLogout} className="hidden sm:inline-flex">
            {t.nav.logout}
          </Button>

          {/* Hamburger — only shown when the desktop nav is hidden, and never
              for the kiosk (there's nothing behind it to open). */}
          {!isKiosk && (
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              className="xl:hidden p-2 -mr-2 text-tremor-content hover:text-tremor-content-strong"
              aria-label="Abrir menú"
              aria-expanded={menuOpen}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                {menuOpen ? (
                  <path d="M6 6l12 12M6 18L18 6" />
                ) : (
                  <path d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Mobile drawer — collapses below the header. Tappable rows with the
          same role-gating as desktop. Closing on each click prevents the
          common "tap nav, page changes, drawer still open" annoyance. Never
          shown for the kiosk (the hamburger that would open it is gone too,
          but this stays explicit rather than relying on menuOpen never
          flipping true). */}
      {!isKiosk && menuOpen && (
        <nav className="xl:hidden border-t border-tremor-border bg-tremor-background">
          {navItems.map((it) =>
            it.kind === 'group'
              ? renderMobileGroup(it.id, it.label, it.children)
              : it.show ? (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  className={mobileNavClass}
                  onClick={() => setMenuOpen(false)}
                >
                  {it.label}
                </NavLink>
              ) : null,
          )}
          <div className="border-t border-tremor-border px-4 py-3 flex items-center justify-between sm:hidden">
            <Text className="text-tremor-content text-sm">
              {user.username} · {t.roles[user.role]}
            </Text>
            <Button size="xs" variant="light" onClick={onLogout}>{t.nav.logout}</Button>
          </div>
        </nav>
      )}
    </header>
  );
}
